import { convexQuery, useConvexMutation } from '@convex-dev/react-query'
import { api } from '@cvx/_generated/api'
import type { Id } from '@cvx/_generated/dataModel'
import { Button } from '@rekrytering/ui/components/button'
import { Input } from '@rekrytering/ui/components/input'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useState } from 'react'

const TAG_COLORS = [
  '#3b82f6',
  '#10b981',
  '#f59e0b',
  '#ef4444',
  '#8b5cf6',
  '#ec4899',
  '#06b6d4',
  '#84cc16',
] as const

type TagColor = (typeof TAG_COLORS)[number]

export const TagsSettingsPage = () => {
  const [newLabel, setNewLabel] = useState('')
  const [newColor, setNewColor] = useState<TagColor>(TAG_COLORS[0])

  const { data: tags = [] } = useQuery(
    convexQuery(api.features.candidates.tags.listMyTags, {}),
  )

  const { mutateAsync: createTag } = useMutation({
    mutationFn: useConvexMutation(api.features.candidates.tags.createMyTag),
  })

  const { mutateAsync: deleteTag } = useMutation({
    mutationFn: useConvexMutation(api.features.candidates.tags.deleteMyTag),
  })

  const handleCreate = async (): Promise<void> => {
    const trimmed = newLabel.trim()
    if (!trimmed) return
    await createTag({ label: trimmed, color: newColor })
    setNewLabel('')
  }

  return (
    <div className="flex w-full flex-col gap-6">
      <div>
        <h2 className="text-base font-semibold text-primary">Tags</h2>
        <p className="mt-1 text-sm text-primary/60">
          Create color-coded tags to organize candidates and jobs.
        </p>
      </div>

      <div className="flex items-center gap-2">
        <Input
          className="w-64"
          placeholder="New tag label..."
          value={newLabel}
          onChange={(e) => setNewLabel(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void handleCreate()
          }}
        />
        <div className="flex gap-1">
          {TAG_COLORS.map((color) => (
            <button
              key={color}
              type="button"
              className="h-6 w-6 rounded-full border-2 transition-transform hover:scale-110"
              style={{
                backgroundColor: color,
                borderColor: newColor === color ? color : 'transparent',
                outline: newColor === color ? `2px solid ${color}` : 'none',
                outlineOffset: '2px',
              }}
              onClick={() => setNewColor(color)}
            />
          ))}
        </div>
        <Button size="sm" onClick={() => void handleCreate()}>
          Add
        </Button>
      </div>

      {tags.length === 0 ? (
        <p className="text-sm text-primary/40">No tags defined yet.</p>
      ) : (
        <ul className="flex flex-col gap-2">
          {tags.map((tag) => (
            <li
              key={tag._id}
              className="flex items-center justify-between gap-4 rounded-lg border border-border px-4 py-3"
            >
              <div className="flex items-center gap-2">
                <span
                  className="h-3 w-3 rounded-full"
                  style={{ backgroundColor: tag.color }}
                />
                <span className="text-sm text-primary">{tag.label}</span>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={() => void deleteTag({ tagId: tag._id as Id<'tags'> })}
              >
                Delete
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
